import { useEffect } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import {
  DEFAULT_POCKET_COLOR,
  DEFAULT_POCKET_ICON,
  POCKET_COLOR_PALETTE,
  POCKET_ICONS,
  resolvePocketName,
} from "../constants/pockets.constants";
import { PocketsService } from "../services/pocketsService";
import type { Pocket, WalletIcon } from "../types/pockets-api.types";
import { PocketFormFields } from "./PocketFormFields";

const ALLOWED_ICONS = POCKET_ICONS.map((d) => d.id) as [WalletIcon, ...WalletIcon[]];
const ALLOWED_COLORS = POCKET_COLOR_PALETTE as readonly string[];

const schema = z.object({
  name: z.string().min(1).max(255),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .refine((v) => ALLOWED_COLORS.includes(v), {
      message: "Color must be one of the picker swatches.",
    }),
  icon: z.enum(ALLOWED_ICONS),
});

type Values = z.infer<typeof schema>;

const FORM_ID = "edit-pocket-form";

const valuesFromPocket = (pocket: Pocket): Values => ({
  name: resolvePocketName(pocket),
  color: pocket.metadata?.color || DEFAULT_POCKET_COLOR,
  icon: (pocket.metadata?.icon as WalletIcon | null) || DEFAULT_POCKET_ICON,
});

type EditPocketModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pocket: Pocket | null;
  onUpdated?: (pocket: Pocket) => void;
};

export const EditPocketModal = ({ open, onOpenChange, pocket, onUpdated }: EditPocketModalProps) => {
  const { t } = useTranslations("cards");
  const form = useZodForm(
    schema,
    pocket ? valuesFromPocket(pocket) : { name: "", color: DEFAULT_POCKET_COLOR, icon: DEFAULT_POCKET_ICON },
  );

  // `form` excluded from deps — fresh object every render would reset edits
  // on every keystroke. RHF refs survive renders.
  useEffect(() => {
    if (pocket) form.reset(valuesFromPocket(pocket));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pocket?.id]);

  const handleSubmit = async (values: Values) => {
    if (!pocket) return;
    try {
      const response = await PocketsService.updatePocket(pocket.id, {
        name: values.name.trim(),
        color: values.color,
        icon: values.icon,
      });
      colocaToast.success(t("pocket_update_success"));
      onUpdated?.(response.data);
      onOpenChange(false);
    } catch (err: unknown) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("pocket_update_error"));
    }
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("pocket_edit_modal_title")}
      description={t("pocket_edit_modal_description")}
      size="lg"
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <ColocaButton variant="ghost" onClick={() => onOpenChange(false)}>
            {t("create_identity_modal_button_cancel")}
          </ColocaButton>
          <ColocaButton type="submit" variant="primary" form={FORM_ID}>
            {t("pocket_edit_modal_submit")}
          </ColocaButton>
        </div>
      }
    >
      <ColocaForm form={form} onSubmit={handleSubmit} id={FORM_ID}>
        <PocketFormFields />
      </ColocaForm>
    </ColocaDialog>
  );
};
